import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet'; // SEO Essential
import { ArrowRight, Star } from 'lucide-react';
import { Link } from 'react-router-dom';

const HeroPage = () => {
  const [currentSlide, setCurrentSlide] = useState(0);

  // UPDATED: Keywords injected into Titles & Subtitles naturally
  const slides = [
    {
      img: "https://images.unsplash.com/photo-1600210492486-724fe5c67fb0?q=80&w=2074&auto=format&fit=crop",
      title: "Luxury Interior Design", // Keyword: Interior Design
      sub: "Premium Turnkey Solutions for Modern Homes", // Keyword: Turnkey Solutions
      alt: "Luxury Living Room Interior Design in High-End Apartment" // Alt Text SEO
    },
    {
      img: "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?q=80&w=2053&auto=format&fit=crop",
      title: "Royal Home Renovation", // Keyword: Home Renovation
      sub: "Crafting Legacy with Gold & Velvet Aesthetics",
      alt: "Royal Bedroom Renovation with Gold Accents and Vintage Furniture"
    },
    {
      img: "https://images.unsplash.com/photo-1631679706909-1844bbd07221?q=80&w=1992&auto=format&fit=crop",
      title: "Architectural Planning", // Keyword: Architecture
      sub: "Expert Builders & Space Planning Services", // Keyword: Builders
      alt: "Modern Architectural Design and Interior Planning for Villas"
    }
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [slides.length]);

  // UPDATED: Schema Markup for Local Business (Better for Leads than Organization)
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "HomeAndConstructionBusiness", // Specific Type for Leads
    "name": "Luxuriya Homes",
    "url": "https://www.luxuriyahomes.com",
    "logo": "https://www.luxuriyahomes.com/logo.png",
    "image": slides[0].img,
    "description": "Luxuriya Homes provides premium interior design, home renovation, and architecture services. Top-rated luxury builders and turnkey contractors.",
    "priceRange": "$$$",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Noida", // Apni City yahan dalein
      "addressRegion": "UP",
      "addressCountry": "IN"
    },
    "sameAs": [
      "https://www.instagram.com/luxuriyahomes",
      "https://www.facebook.com/luxuriyahomes"
    ]
  };

  return (
    <div className="bg-white text-black font-sans selection:bg-amber-200 selection:text-black overflow-x-hidden">
      
      {/* --- SEO HEAD SECTION --- */}
      <Helmet>
        {/* Title Optimized for CTR and Keywords */}
        <title>Luxuriya Homes | Best Luxury Interior Designers & Home Renovators</title>
        
        {/* Meta Description: Includes Keywords + Call to Action (CTA) */}
        <meta name="description" content="Looking for premium interior design? Luxuriya Homes specializes in luxury home renovations, modular kitchens, and architectural planning. Get a free consultation today!" />
        
        {/* Keywords: Mix of Services and Intent */}
        <meta name="keywords" content="luxury interior design, home renovation services, turnkey interior contractors, modular kitchen designers, best architects, royal home decor, villa renovation" />
        
        <link rel="canonical" href="https://www.luxuriyahomes.com/" />
        
        {/* Open Graph */}
        <meta property="og:title" content="Transform Your Home with Luxuriya Homes - Premium Interiors" />
        <meta property="og:description" content="Rated #1 for Luxury Interiors. We offer end-to-end home renovation and design services." />
        <meta property="og:image" content={slides[0].img} />
        <meta property="og:type" content="business.business" />

        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(organizationSchema)}
        </script>
      </Helmet>

      {/* 1. HERO SLIDER SECTION */}
      <section className="relative h-[92vh] w-full overflow-hidden bg-gray-900" aria-label="Luxury Design Portfolio Slider">
        
        {slides.map((slide, index) => (
          <div 
            key={index}
            className={`absolute inset-0 w-full h-full transition-opacity duration-1000 ease-in-out ${
              index === currentSlide ? 'opacity-100 z-10' : 'opacity-0 z-0'
            }`}
          >
            <img 
              src={slide.img} 
              alt={slide.alt} // Dynamic SEO Alt Text
              fetchPriority={index === 0 ? "high" : "auto"}
               loading={index === 0 ? "eager" : "lazy"}
              className={`w-full h-full object-cover ${index === currentSlide ? 'animate-subtle-zoom' : ''}`}
            />
            <div className="absolute inset-0 bg-black/30"></div>
          </div>
        ))}

        <div className="absolute top-0 left-0 w-full h-48 bg-gradient-to-b from-black/90 via-black/40 to-transparent z-10 pointer-events-none"></div>
        <div className="absolute bottom-0 left-0 w-full h-48 bg-gradient-to-t from-black/90 via-black/40 to-transparent z-10 pointer-events-none"></div>

        {/* Hero Content */}
        <div className="absolute inset-0 z-20 flex flex-col items-center justify-center text-center px-4">
           <div className="animate-fade-in-up mt-10">
              <p className="text-amber-300 tracking-[0.4em] uppercase text-xs md:text-sm mb-6 font-bold shadow-black drop-shadow-md">
                 {slides[currentSlide].sub}
              </p>
              {/* Main H1 - Using Keywords */}
              <h1 className="text-4xl md:text-7xl font-serif text-white mb-10 drop-shadow-2xl max-w-4xl leading-tight">
                {slides[currentSlide].title}
              </h1>
              
              {/* Dual CTA Buttons for Higher Conversion */}
              <div className="flex gap-4 justify-center flex-col md:flex-row">
                <Link 
                    to="/contact" 
                    className="inline-block px-10 py-4 bg-amber-600 border border-amber-600 text-white hover:bg-amber-700 transition-all duration-300 uppercase tracking-widest text-xs font-bold shadow-lg"
                    aria-label="Book a free consultation"
                >
                    Book Consultation
                </Link>
                <Link 
                    to="/projects" 
                    className="inline-block px-10 py-4 bg-transparent border border-white/50 text-white hover:bg-white hover:text-black transition-all duration-300 uppercase tracking-widest text-xs backdrop-blur-md"
                    aria-label="View our luxury projects"
                >
                    View Projects
                </Link>
              </div>
           </div>
        </div>

        {/* Indicators */}
        <div className="absolute bottom-12 right-8 z-30 flex gap-3">
          {slides.map((_, idx) => (
            <button
              key={idx}
              onClick={() => setCurrentSlide(idx)}
              aria-label={`Show ${slides[idx].title}`}
              className={`h-1.5 rounded-full transition-all duration-300 ${
                currentSlide === idx ? 'w-8 bg-amber-400' : 'w-2 bg-white/40 hover:bg-white'
              }`}
            />
          ))}
        </div>
      </section>

      {/* 2. FLOATING INFO BAR - Added Keyword Rich Text */}
      <div className="relative z-30 -mt-16 max-w-5xl mx-auto px-4">
         <div className="bg-white shadow-[0_20px_50px_rgba(0,0,0,0.3)] flex flex-col md:flex-row justify-between items-center p-8 rounded-sm border-t-4 border-amber-600">
            <div className="mb-6 md:mb-0 text-center md:text-left">
               <p className="text-xs text-gray-400 uppercase tracking-widest mb-1">Delivered Projects</p>
               <p className="text-2xl font-serif text-gray-900">150+ <span className="text-base text-gray-500 font-sans">Luxury Homes</span></p>
            </div>
            
            <div className="hidden md:block h-10 w-[1px] bg-gray-200"></div>
            
            <div className="mb-6 md:mb-0 text-center md:text-left">
               <p className="text-xs text-gray-400 uppercase tracking-widest mb-1">Top Rated</p>
               <div className="flex items-center gap-2 justify-center md:justify-start">
                 <Star size={18} className="text-amber-500 fill-amber-500"/>
                 <p className="text-lg font-serif text-gray-900">#1 Interior Designers</p>
              </div>
            </div>
            
            <div className="hidden md:block h-10 w-[1px] bg-gray-200"></div>
            
            <Link to="/contact" className="px-8 py-3 bg-black text-white text-xs uppercase tracking-widest hover:bg-amber-700 transition-colors shadow-lg">
               Get Free Quote
            </Link>
         </div>
      </div>

    </div>
  );
};

export default HeroPage;